import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Users, MapPin, Phone, Ticket, User, Clock,
  ChevronDown, ChevronUp, Anchor, Mail, Edit
} from "lucide-react";
import { getPaymentState } from "../utils/payment";

/**
 * Reusable Booking Card Component
 * Used in: Schedule Tab, Bookings Calendar Day Modal
 * 
 * Props:
 * - booking: Booking data object
 * - slotColor: Left border color (optional)
 * - onEdit: Callback when Edit button clicked (optional)
 * - onContact: Callback for contact modal ({ type, value, name })
 * - onOpenTicket: Callback when Ticket button clicked (opens in same screen for iOS compatibility)
 * - showEditButton: Show the Edit button (default: false)
 */

// Format duration
const formatDuration = (minutes) => {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}hr`;
  return `${hours}hr ${mins}min`;
};

// Format time to 12-hour with AM/PM
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  
  // Handle various formats: "09:00", "09:00:00", "9:00 AM"
  // If already has AM/PM, return as-is
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr;
  }
  
  // Parse 24-hour format
  const [hourStr, minuteStr] = timeStr.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr ? minuteStr.substring(0, 2) : '00';
  
  if (isNaN(hour)) return timeStr;
  
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  
  return `${hour}:${minute} ${ampm}`;
};

const BookingCard = ({ 
  booking, 
  slotColor = '#3B82F6',
  onEdit,
  onContact,
  onOpenTicket,
  showEditButton = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const duration = formatDuration(booking.duration_minutes || booking.durationMinutes);

  // Payment state — single source of truth (amount-driven, catches partial
  // payments even when payment_status was mislabelled 'paid')
  const pay = getPaymentState(booking);
  
  // Build location string
  const locationParts = [booking.city, booking.country].filter(Boolean);
  const locationStr = locationParts.join(', ');
  
  // System fields to exclude from customer responses
  const SYSTEM_FIELDS = new Set([
    'currency', 'conversionRate', 'isMultiCurrency', 'presentmentCurrency',
    'selectedAddOns', 'customAnswers', 'referralCode', 'distributionRef',
    'migration', 'promoCode'
  ]);
  
  const addOns = booking.add_ons || booking.selectedAddOns || [];
  const promoCode = booking.promo_code || booking.promoCode;
  const meaningfulResponses = (booking.custom_responses || booking.customAnswers || [])
    .filter(r => r && r.question && !SYSTEM_FIELDS.has(r.question) && 
                 r.answer !== null && r.answer !== undefined && r.answer !== '' && r.answer !== false);

  // Always show expand toggle — when empty it says "No additional info"
  // When populated shows add-ons, custom questions, promo codes
  const hasExpandableContent = true;  // always show so operators know state

  // Get team display text
  const getTeamDisplay = () => {
    if (booking.team_assignments?.length > 0) {
      const names = booking.team_assignments
        .filter(a => a.teamName)
        .map(a => a.teamName);
      return names.length > 0 ? names.join(', ') : 'Unassigned';
    }
    // Also check teamAssignments (camelCase from API)
    if (booking.teamAssignments?.length > 0) {
      const names = booking.teamAssignments
        .filter(a => a.teamName)
        .map(a => a.teamName);
      return names.length > 0 ? names.join(', ') : 'Unassigned';
    }
    return booking.team_member_name || booking.teamMemberName || 'Unassigned';
  };

  const hasTeamAssigned = 
    (booking.team_assignments?.length > 0 && booking.team_assignments.some(a => a.teamName)) ||
    (booking.teamAssignments?.length > 0 && booking.teamAssignments.some(a => a.teamName)) ||
    booking.team_member_name || booking.teamMemberName;

  // Handle ticket click - navigate in same screen (iOS compatible)
  const handleTicketClick = () => {
    const ticketUrl = booking.ticket_url || booking.ticketUrl;
    if (ticketUrl) {
      // Extract the path from the URL and navigate within app
      try {
        const url = new URL(ticketUrl);
        navigate(url.pathname + url.search);
      } catch {
        // If URL parsing fails, use as-is or call callback
        if (onOpenTicket) {
          onOpenTicket(ticketUrl);
        }
      }
    }
  };

  // Get asset name from either snake_case or camelCase
  const getAssetName = () => {
    return booking.asset_name || booking.assetName || null;
  };

  return (
    <div
      data-testid={`booking-card-${booking.confirmation_code || booking.confirmationCode}`}
      className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-shadow"
      style={{ borderLeft: `4px solid ${slotColor}` }}
    >
      <div className="p-4 sm:p-5 space-y-2.5">
        {/* ROW 1: Time pill + timezone abbr + Service name | Action buttons */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-sm font-semibold whitespace-nowrap">
                {formatTime12Hour(booking.start_time || booking.startTime)}
                {booking.timezone && (
                  <span className="ml-1 font-normal opacity-70 text-xs">
                    {booking.timezone.split('/').pop()?.replace(/_/g, ' ') === 'Chicago' ? 'CST' :
                     booking.timezone.split('/').pop()?.replace(/_/g, ' ') === 'New_York' ? 'EST' :
                     booking.timezone.split('/').pop()?.replace(/_/g, ' ') === 'Los_Angeles' ? 'PST' :
                     booking.timezone.split('/').pop()?.replace(/_/g, ' ') === 'Denver' ? 'MST' :
                     booking.timezone.split('/').pop()?.replace(/_/g, ' ')?.slice(0, 3).toUpperCase()}
                  </span>
                )}
              </span>
              <h3 className="font-semibold text-gray-900 truncate">
                {booking.service_name || booking.serviceName}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(booking.ticket_url || booking.ticketUrl) && (
              <Button variant="ghost" size="sm" onClick={handleTicketClick}
                data-testid="booking-ticket-btn"
                className="h-8 gap-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 px-2.5">
                <Ticket className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Ticket</span>
              </Button>
            )}
            {showEditButton && onEdit && (
              <Button variant="ghost" size="sm" onClick={() => onEdit(booking)}
                data-testid="booking-edit-btn"
                className="h-8 gap-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 px-2.5">
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Edit</span>
              </Button>
            )}
          </div>
        </div>

        {/* ROW 2: Customer name | Payment status (right) + contact icons */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <User className="w-3 h-3 text-gray-500" />
            </div>
            <span className="font-medium text-gray-900 truncate text-sm">
              {booking.customer_name || booking.customerName}
            </span>
            {/* Payment — subtle dot-status text, derived from actual amounts.
                Partial payments show the figures inline: · Paid $X · Owes $Y */}
            {(booking.payment_status || booking.paymentStatus) && (
              pay.state === 'deposit' ? (
                <span className="text-xs flex-shrink-0">
                  <span className="text-green-600">· Paid ${Number(pay.paid).toFixed(2)}</span>
                  <span className="text-amber-600 font-semibold"> · Owes ${Number(pay.remaining).toFixed(2)}</span>
                </span>
              ) : (
                <span className={`text-xs font-normal flex-shrink-0 ${
                  (booking.payment_status || booking.paymentStatus) === 'refunded' ? 'text-gray-400' :
                  pay.state === 'paid' ? 'text-green-600' : 'text-red-500'
                }`}>
                  · {(booking.payment_status || booking.paymentStatus) === 'refunded' ? 'Refunded' :
                     pay.state === 'paid' ? 'Paid' : 'Unpaid'}
                </span>
              )
            )}
            {/* Migrated — same subtle dot-status style */}
            {(booking.booking_source === 'legacy-import' || booking.bookingSource === 'legacy-import' || booking.source === 'migration') && (
              <span
                className="text-xs font-normal text-purple-600 flex-shrink-0"
                data-testid="migrated-badge"
                title="Migrated from the operator's previous booking system"
              >
                · Migrated
              </span>
            )}
            {/* Needs boat — external/serviceless bookings with no asset assigned.
                These block NOTHING on the calendar until a boat is assigned. */}
            {!getAssetName() && booking.status !== 'cancelled' &&
              (booking.service_id === null || booking.serviceId === null ||
               ['getmyboat', 'boatsetter', 'manual_import', 'external'].includes(
                 booking.booking_source || booking.bookingSource || '')) && (
              <span
                className="text-xs font-semibold text-amber-600 flex-shrink-0"
                data-testid="needs-asset-badge"
                title="No boat assigned — this booking does NOT block any availability until a boat is assigned"
              >
                · Needs boat ⚠
              </span>
            )}
            {(booking.status === 'cancelled' || booking.status === 'checked_in') && (
              <span className={`text-xs flex-shrink-0 ${
                booking.status === 'cancelled' ? 'text-red-500' : 'text-green-600'
              }`}>
                · {booking.status === 'cancelled' ? 'Cancelled' : 'Checked In'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {(booking.customer_phone || booking.customerPhone) && onContact && (
              <button onClick={() => onContact({ type: 'phone', value: booking.customer_phone || booking.customerPhone, name: booking.customer_name || booking.customerName, booking })}
                className="w-7 h-7 rounded-full bg-gray-100 hover:bg-green-50 hover:text-green-600 flex items-center justify-center text-gray-400 transition-colors">
                <Phone className="w-3.5 h-3.5" />
              </button>
            )}
            {(booking.customer_email || booking.customerEmail) && onContact && (
              <button onClick={() => onContact({ type: 'email', value: booking.customer_email || booking.customerEmail, name: booking.customer_name || booking.customerName, booking })}
                className="w-7 h-7 rounded-full bg-gray-100 hover:bg-blue-50 hover:text-blue-600 flex items-center justify-center text-gray-400 transition-colors">
                <Mail className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* ROW 3: Location */}
        {(locationStr || booking.meeting_point || booking.pickupLocation) && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500">
            <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
            <span>
              {locationStr && <span className="text-gray-700">{locationStr}</span>}
              {(booking.meeting_point || booking.pickupLocation) && (
                <span className="text-gray-400"> · {booking.meeting_point || booking.pickupLocation}</span>
              )}
            </span>
          </div>
        )}

        {/* ROW 4: Asset + Team — show asset when set, show team when asset is set */}
        {(getAssetName() || hasTeamAssigned) && (
          <div className="flex items-center gap-4 text-xs text-gray-500">
            {getAssetName() && (
              <div className="flex items-center gap-1.5">
                <Anchor className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-gray-700">{getAssetName()}</span>
              </div>
            )}
            {/* Show team row when asset is set (may have requirements) OR team is assigned */}
            {(getAssetName() || hasTeamAssigned) && (
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-gray-400" />
                <span className={hasTeamAssigned ? 'text-gray-700' : 'text-gray-400'}>
                  {hasTeamAssigned ? getTeamDisplay() : 'Unassigned'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* FOOTER: Ref · Duration · Guests — small, muted */}
        <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-100">
          <div>
            {/* See more / expand toggle — always visible so operators know booking state */}
            {hasExpandableContent && (
              <button
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
                className="flex items-center gap-1 text-xs text-blue-500 font-medium hover:text-blue-700 transition-colors"
              >
                {isExpanded ? 'Less' : 'More info'}
                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
            <span>{booking.confirmation_code || booking.confirmationCode}</span>
            {duration && <><span>·</span><span>{duration}</span></>}
            <span>·</span>
            <span>{booking.party_size || booking.partySize || 1}p</span>
          </div>
        </div>
      </div>
      
      {/* EXPANDED SECTION */}
      {isExpanded && (
        <div className="px-4 sm:px-5 py-4 bg-gray-50 space-y-4 border-t border-gray-100">
          {/* Add-ons */}
          {addOns.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Add-ons</p>
              <div className="flex flex-wrap gap-1.5">
                {addOns.map((addon, idx) => {
                  const name  = addon?.title || addon?.name || addon?.addOnName || (typeof addon === 'string' ? addon : '—');
                  const price = addon?.price != null ? ` · $${addon.price}` : '';
                  const qty   = addon?.quantity > 1 ? `${addon.quantity}× ` : '';
                  return (
                    <span key={idx} className="text-xs bg-white border border-gray-200 rounded-full px-2.5 py-1 text-gray-700">
                      {qty}{name}{price}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No add-ons</p>
          )}

          {/* Promo code */}
          {promoCode && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 uppercase tracking-wide">Promo</span>
              <span className="text-xs font-mono bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-700">{promoCode}</span>
            </div>
          )}

          {/* Gift card (Phase 4.5) — surfaced when booking had a gift card applied */}
          {(booking.gift_card_amount || booking.giftCardAmount) > 0 && (
            <div className="flex items-center gap-2" data-testid="booking-gift-card-badge">
              <span className="text-xs text-gray-400 uppercase tracking-wide">Gift card</span>
              <span className="text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">
                −${Number(booking.gift_card_amount || booking.giftCardAmount).toFixed(2)} applied
              </span>
            </div>
          )}

          {/* Customer Q&A */}
          {meaningfulResponses.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Customer Responses</p>
              <div className="space-y-1.5">
                {meaningfulResponses.map((resp, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-xs">
                    <span className="text-gray-400 flex-shrink-0 min-w-[90px] truncate">{resp.question}</span>
                    <span className="text-gray-800 font-medium">{String(resp.answer)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No custom responses</p>
          )}

          {/* Notes */}
          {(booking.customer_notes || booking.customerNotes || booking.internal_notes || booking.internalNotes) && (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Notes</p>
              {(booking.customer_notes || booking.customerNotes) && (
                <p className="text-xs text-gray-600">{booking.customer_notes || booking.customerNotes}</p>
              )}
              {(booking.internal_notes || booking.internalNotes) && (
                <p className="text-xs text-gray-500 italic mt-0.5">Internal: {booking.internal_notes || booking.internalNotes}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingCard;
